/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, October 1st 2021, 3:28:31 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import FormControl from "@mui/material/FormControl";
import { ADMIN_PORTAL_DEPLOYMENT_MODE, ALLOW_MULTI_ORGANIZATION_FOR_ON_PREMISE } from "config";
import { ActionSnackbar, useToggleState } from "ui-widgets";
import { Organization, OrganizationListItem } from "lib/schemas";
import { useGlobalScope } from "core/providers";
import { OrganizationLoadingDialog } from "./OrganizationLoadingDialog";

/*
 *  Stateful Organizations Dropdown. This is used to set the global organization scope of the application via the
 *  React Context API
 *
 */

interface IRenderSelectorComponentArgs {
  switchOrganization: (pickedOrganization: Organization | OrganizationListItem) => void;
  selectedOrganization: Organization | OrganizationListItem;
}

interface OrganizationScopeSelectorProps {
  renderSelectorComponent: (args: IRenderSelectorComponentArgs) => React.ReactNode;
}

export const OrganizationScopeSelector = ({ renderSelectorComponent }: OrganizationScopeSelectorProps) => {
  const {
    handleClose: closeSuccessSwitchSnack,
    open: successSwitchSnackOpenState,
    handleOpen: openSuccessSwitchSnack,
  } = useToggleState();

  const globalContext = useGlobalScope();

  const switchOrganization = (pickedOrganization: OrganizationListItem) => {
    globalContext.updateGlobalOrganizationScope({
      selectedOrganizationId: pickedOrganization.id,
    });
  };

  const previousOrganizationIdRef = React.useRef<OrganizationListItem["id"]>();

  const {
    globalState: { selectedOrganization },
  } = globalContext;

  React.useEffect(() => {
    const previousOrganizationId = previousOrganizationIdRef.current;

    if (previousOrganizationId && selectedOrganization && previousOrganizationId !== selectedOrganization.id) {
      openSuccessSwitchSnack();
    }

    previousOrganizationIdRef.current = selectedOrganization?.id;
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganization?.id]);

  const showOrganizationSelector =
    ADMIN_PORTAL_DEPLOYMENT_MODE === "SAAS" || ALLOW_MULTI_ORGANIZATION_FOR_ON_PREMISE || !selectedOrganization?.id;

  return showOrganizationSelector ? (
    <FormControl className="sidebar-org-switch" sx={{ margin: "auto 1em" }}>
      {globalContext.globalState.selectedOrganization && (
        <ActionSnackbar
          message={`Organization scope changed to ${globalContext.globalState.selectedOrganization?.name}`}
          actionText="Got it"
          actionFunction={closeSuccessSwitchSnack}
          open={successSwitchSnackOpenState}
          handleClose={closeSuccessSwitchSnack}
          handleOpen={openSuccessSwitchSnack}
        />
      )}

      {globalContext.globalState.isFetchingGlobalOrganization ? <OrganizationLoadingDialog /> : null}

      {renderSelectorComponent({ switchOrganization, selectedOrganization })}
    </FormControl>
  ) : null;
};
